/**
 * Runtime tenant configuration (P1, domain-model-transition).
 *
 * Holds the payload of GET /api/config for the signed-in tenant. The baked
 * environment descriptors (`environments/*`) become the FALLBACK: any key the
 * fetched config does not carry keeps its descriptor value, so a tenant with an
 * empty `features` object behaves exactly as the build did before P1 — and a
 * deployment whose backend has no `tenants` row yet (404) stays on the
 * descriptor entirely. That fallback is load-bearing until P9 retires the
 * descriptors; do not remove it here.
 *
 * Module-level store rather than React context because the main consumers are
 * plain functions (`isFeatureEnabled` in AppSettings/environmentConfig.ts)
 * called from dozens of components; reactive consumers use `useRuntimeConfig`.
 */

import { useSyncExternalStore } from 'react';

export interface RuntimeTenantConfig {
  tenant: {
    displayName: string;
    brandLabel: string | null; // NULL means "use the build's baked label"
    isActive: boolean;
  };
  // Mostly boolean flags; string-valued entries carry per-tenant codes the UI needs
  // (P3: `projectTypeCode`). `isFeatureEnabled` coerces with Boolean(), so a string
  // entry read as a flag is simply truthy — harmless in both directions.
  features: Record<string, boolean | string>;
  invoiceSender: {
    name: string | null;
    address: string | null;
    email: string | null;
    phone: string | null;
    gstNumber: string | null;
  };
  defaults: {
    currency: string;
    workweekStartDay: number;
    jurisdiction: string | null;
  };
}

/**
 * Why a status and not just `current === null`.
 *
 * A bare null conflates three DIFFERENT situations, and consumers need to tell them apart:
 *   'pending'     — the fetch has not resolved yet. Nothing is known.
 *   'ready'       — a config was fetched. Its fields are this tenant's, including empty ones.
 *   'unavailable' — the fetch definitively failed or the deployment has no tenant row (404).
 *
 * The invoice wizard is the case that forced this: an unset sender on a 'ready'/'unavailable'
 * config legitimately means "unregistered deployment, use the product fallback", whereas the
 * same emptiness during 'pending' means "we do not know yet" — and guessing there is how one
 * tenant's letterhead and GST number end up on another tenant's invoice.
 */
export type RuntimeConfigStatus = 'pending' | 'ready' | 'unavailable';

let current: RuntimeTenantConfig | null = null;
let status: RuntimeConfigStatus = 'pending';
let loadStarted = false;
const listeners = new Set<() => void>();

export const getRuntimeConfig = (): RuntimeTenantConfig | null => current;

export const getRuntimeConfigStatus = (): RuntimeConfigStatus => status;

export const setRuntimeConfig = (config: RuntimeTenantConfig | null): void => {
  current = config;
  status = config ? 'ready' : 'unavailable';
  listeners.forEach((listener) => listener());
};

/** The fetch resolved to "no usable config" — failed, or no tenant row for this deployment. */
export const markRuntimeConfigUnavailable = (): void => {
  status = 'unavailable';
  listeners.forEach((listener) => listener());
};

/** Test seam: reset the store between tests. */
export const resetRuntimeConfig = (): void => {
  current = null;
  status = 'pending';
  loadStarted = false;
  listeners.forEach((listener) => listener());
};

export const subscribeRuntimeConfig = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Reactive read — re-renders the caller when the config arrives or changes. */
export const useRuntimeConfig = (): RuntimeTenantConfig | null =>
  useSyncExternalStore(subscribeRuntimeConfig, getRuntimeConfig, getRuntimeConfig);

/** Reactive status — for consumers that must not act on an unresolved config. */
export const useRuntimeConfigStatus = (): RuntimeConfigStatus =>
  useSyncExternalStore(subscribeRuntimeConfig, getRuntimeConfigStatus, getRuntimeConfigStatus);

/**
 * Fetch-once loader. Takes the fetcher as an argument so this low-level module
 * never imports the service layer. Any failure (401 pre-auth, 404 unregistered
 * tenant, network) leaves the store empty — i.e. the pre-P1 descriptor
 * behaviour — and logs once.
 */
export const loadRuntimeConfig = async (
  fetcher: () => Promise<RuntimeTenantConfig>,
): Promise<void> => {
  if (loadStarted) return;
  loadStarted = true;
  try {
    const config = await fetcher();
    // Shape guard: a mocked or misrouted response (e.g. the L1 catch-all's `[]`,
    // or a proxy serving `features: null`) must never poison the store —
    // malformed means "unavailable", i.e. fallback.
    if (
      !config ||
      typeof config !== 'object' ||
      typeof config.features !== 'object' ||
      config.features === null
    ) {
      throw new Error('Malformed runtime config payload');
    }
    setRuntimeConfig(config);
  } catch (error) {
    loadStarted = false; // a later successful auth may retry
    // Resolved, just not usable. Leaving this 'pending' would block every consumer that
    // waits for a definite answer — including invoice generation on a deployment that has
    // no tenant row at all, which is a legitimate steady state, not an error to wait out.
    markRuntimeConfigUnavailable();
    console.warn('Runtime config unavailable; using the baked environment descriptor.', error);
  }
};
